import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSize } from 'ahooks';
import { Affix, type GetProps, Tabs } from 'antd';
import './style.less';

type TabsType = GetProps<typeof Tabs>;

export type TabsStickyProps = {
  tabsProps: Omit<TabsType, 'tabPosition'>;
  /** tabs头部布局 */
  headerRender?: ReactElement;
  /** 点击tabItem后，tab跳到顶部吸顶效果；默认值true */
  clickTabToTop?: boolean;
  /** 设置tabItem 内容包裹元素的样式 */
  tabItemContentWrapperStyle?: (key: string) => CSSProperties;
};

/**
 * Tabs吸顶组件
 * ```
 * 1. 组件根节点设置了 height: 100%、overflow: auto；所以外部包裹元素必须要有高度
 * ```
 */
export const TabsSticky = (props: TabsStickyProps) => {
  const tabsProps = props.tabsProps;

  const [activeKey, setActiveKey] = useState(tabsProps.defaultActiveKey);
  const headerRef = useRef<HTMLDivElement>(null);
  const affixRef = useRef<any>(null);
  const affixedFlagRef = useRef<boolean>();

  const headerSize = useSize(headerRef);

  const scollNodeRef = useRef<any>(null);

  const scollNodeSize = useSize(scollNodeRef);
  const affixRefSize = useSize(affixRef);

  useEffect(() => {
    if (tabsProps.activeKey) {
      setActiveKey(tabsProps.activeKey);
    }
  }, [tabsProps.activeKey]);

  const onChange = (activeKey) => {
    if (!tabsProps.activeKey) {
      setActiveKey(activeKey);
    }
    tabsProps.onChange?.(activeKey);
    const top = headerSize?.height || 0;
    if (affixedFlagRef.current) {
      scollNodeRef.current?.scrollTo({
        top,
      });
    } else {
      if (props.clickTabToTop !== false) {
        scollNodeRef.current?.scrollTo({
          top,
        });
      }
    }
  };

  const minHeight = (scollNodeSize?.height || 0) - (affixRefSize?.height || 0);

  const stickyTabItems = useMemo(() => {
    return tabsProps.items?.map((item) => {
      return {
        ...item,
        children: null,
      };
    });
  }, [tabsProps.items]);

  const contentTabItems = useMemo(() => {
    return tabsProps.items?.map((item) => {
      const wrapperStyle = props.tabItemContentWrapperStyle?.(item.key);
      return {
        ...item,
        children: (
          <div
            className="tabs-sticky-contents-item"
            style={{
              minHeight,
              ...wrapperStyle,
            }}
          >
            {item.children}
          </div>
        ),
      };
    });
  }, [tabsProps.items, minHeight, props.tabItemContentWrapperStyle]);

  return (
    <div ref={scollNodeRef} style={{ height: '100%', overflowY: 'auto' }}>
      <div ref={headerRef}>{props.headerRender}</div>
      <Affix
        offsetTop={0}
        target={() => scollNodeRef.current as HTMLElement}
        onChange={(affixed) => {
          affixedFlagRef.current = affixed;
        }}
      >
        <div ref={affixRef}>
          <Tabs
            {...tabsProps}
            activeKey={activeKey}
            items={stickyTabItems}
            tabBarStyle={{ ...tabsProps.tabBarStyle, margin: 0 }}
            style={{
              backgroundColor: 'var(--block-bg-color)',
              ...tabsProps.style,
            }}
            onChange={onChange}
          />
        </div>
      </Affix>
      <Tabs
        activeKey={activeKey}
        className="tabs-sticky-contents"
        items={contentTabItems}
      />
    </div>
  );
};
